/* Dark overlay with highlighted target and step callout for AI-driven guided walkthroughs.
   Adapts the SVG mask pattern from OnboardingHighlightOverlay with click-through support. */

import { useMemo } from "react";
import { createPortal } from "react-dom";
import { X, MousePointerClick } from "lucide-react";
import { useGuidedWalkthroughContext } from "@/hooks/useGuidedWalkthroughContext";

const HIGHLIGHT_PADDING = 8;
const CALLOUT_OFFSET = 14;

export function GuidedWalkthroughOverlay() {
  const {
    isActive,
    currentStep,
    stepIndex,
    totalSteps,
    walkthroughName,
    targetRect,
    cancel,
  } = useGuidedWalkthroughContext();

  const calloutStyle = useMemo((): React.CSSProperties => {
    if (!targetRect) {
      return {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const style: React.CSSProperties = { position: "fixed" };
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const CALLOUT_HEIGHT = 160;
    const CALLOUT_WIDTH = 320;

    let placement = currentStep?.placement ?? "bottom";

    // Wide target check — prefer left placement
    const isWideTarget = targetRect.width > vw * 0.5;
    if (isWideTarget && (placement === "top" || placement === "bottom")) {
      const spaceLeft = targetRect.left;
      if (spaceLeft >= CALLOUT_WIDTH + CALLOUT_OFFSET) {
        placement = "left";
      }
    }

    // Overflow checks
    if (placement === "bottom" && targetRect.bottom + CALLOUT_OFFSET + CALLOUT_HEIGHT > vh) {
      if (targetRect.top - CALLOUT_OFFSET - CALLOUT_HEIGHT > 0) {
        placement = "top";
      } else {
        placement = "left";
      }
    }
    if (placement === "top" && targetRect.top - CALLOUT_OFFSET - CALLOUT_HEIGHT < 0) {
      if (targetRect.bottom + CALLOUT_OFFSET + CALLOUT_HEIGHT <= vh) {
        placement = "bottom";
      } else {
        placement = "left";
      }
    }

    const MARGIN = 12;
    const clampedY = (centerY: number) =>
      Math.max(MARGIN, Math.min(centerY, vh - CALLOUT_HEIGHT - MARGIN));
    const clampedX = (x: number) =>
      Math.max(MARGIN, Math.min(x, vw - CALLOUT_WIDTH - MARGIN));

    const centerX = targetRect.left + targetRect.width / 2 - CALLOUT_WIDTH / 2;

    switch (placement) {
      case "bottom":
        style.top = targetRect.bottom + HIGHLIGHT_PADDING + CALLOUT_OFFSET;
        style.left = clampedX(centerX);
        break;
      case "top":
        style.bottom = vh - targetRect.top + HIGHLIGHT_PADDING + CALLOUT_OFFSET;
        style.left = clampedX(centerX);
        break;
      case "left":
        style.top = clampedY(targetRect.top + targetRect.height / 2 - CALLOUT_HEIGHT / 2);
        style.right = vw - targetRect.left + HIGHLIGHT_PADDING + CALLOUT_OFFSET;
        break;
      case "right":
        style.top = clampedY(targetRect.top + targetRect.height / 2 - CALLOUT_HEIGHT / 2);
        style.left = clampedX(targetRect.right + HIGHLIGHT_PADDING + CALLOUT_OFFSET);
        break;
    }

    return style;
  }, [targetRect, currentStep?.placement]);

  if (!isActive || !currentStep || !targetRect) return null;

  const showAutoAdvanceHint = currentStep.autoAdvanceOnClick !== false;

  const calloutContent = (
    <div
      className="bg-card border border-border rounded-xl p-4 shadow-lg w-80"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <p className="text-[10px] font-medium text-primary uppercase tracking-wider mb-0.5">
            {walkthroughName}
          </p>
          <h3 className="font-semibold text-sm">{currentStep.title}</h3>
        </div>
        <button
          onClick={cancel}
          className="text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label="Cancel walkthrough"
        >
          <X className="size-4" />
        </button>
      </div>
      <p className="text-sm text-muted-foreground mb-3">{currentStep.description}</p>
      {showAutoAdvanceHint && targetRect && (
        <div className="flex items-center gap-1.5 text-xs text-primary mb-3">
          <MousePointerClick className="size-3.5" />
          <span>Click the highlighted element to continue</span>
        </div>
      )}
      <div className="flex items-center">
        <span className="text-xs text-muted-foreground">
          Step {stepIndex + 1} of {totalSteps}
        </span>
      </div>
    </div>
  );

  const overlayContent = (
    <div
      className="fixed inset-0 z-[10000] pointer-events-none"
      role="dialog"
      aria-label="Guided walkthrough"
    >
      {/* Dark overlay with cutout — purely visual, clicks pass through */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="guided-walkthrough-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left - HIGHLIGHT_PADDING}
                y={targetRect.top - HIGHLIGHT_PADDING}
                width={targetRect.width + HIGHLIGHT_PADDING * 2}
                height={targetRect.height + HIGHLIGHT_PADDING * 2}
                rx={8}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#guided-walkthrough-mask)"
        />
      </svg>

      {/* Highlight border around target */}
      {targetRect && (
        <div
          className="fixed rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-transparent pointer-events-none animate-pulse"
          style={{
            left: targetRect.left - HIGHLIGHT_PADDING,
            top: targetRect.top - HIGHLIGHT_PADDING,
            width: targetRect.width + HIGHLIGHT_PADDING * 2,
            height: targetRect.height + HIGHLIGHT_PADDING * 2,
          }}
        />
      )}

      {/* Desktop callout */}
      <div className="hidden md:block pointer-events-auto" style={calloutStyle}>
        {calloutContent}
      </div>

      {/* Mobile callout (fixed bottom) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 p-4 pointer-events-auto">
        {calloutContent}
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
}
